import { useEffect, useState } from 'react';

/**
 * Encapsulates the page-number + debounced-search dance used by both
 * DashboardPoliciesTable and TenantPoliciesTable.
 *
 * - Debounces `search` by `debounceMs` (default 300).
 * - Empty `search` is applied immediately (no debounce) so synchronous
 *   resets (e.g. clearing the filter on tab change) don't fire a stale
 *   fetch with the previous term before the debounce catches up.
 * - Resets `pageNumber` to 1 whenever the debounced search changes.
 * - Caller is responsible for also calling `setPageNumber(1)` on tab change.
 */
export function useServerPagination(search: string, debounceMs: number = 300) {
  const [pageNumber, setPageNumber] = useState(1);
  const [debouncedSearch, setDebouncedSearch] = useState(search);

  useEffect(() => {
    // Apply clears synchronously to avoid a stale-search fetch on reset.
    if (search === '') {
      setDebouncedSearch('');
      return;
    }
    const t = setTimeout(() => setDebouncedSearch(search), debounceMs);
    return () => clearTimeout(t);
  }, [search, debounceMs]);

  useEffect(() => {
    setPageNumber(1);
  }, [debouncedSearch]);

  return { pageNumber, setPageNumber, debouncedSearch };
}
